import { useCallback, useEffect, useState } from 'react';
import Navbar from '@/components/Navbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { ContentType, GenerationCosts, formatCredits } from '@/lib/creditCosts';

// The platform-wide AI generation price list (backend: services/generationCostService.js). One
// price per asset type for every workspace, because the n8n workflow uses the same model for all of
// them — when the model changes, the price changes here.
const TYPES: { key: ContentType; label: string; hint: string }[] = [
  { key: 'text', label: 'Text (ad copy)', hint: 'One ad-copy generation.' },
  { key: 'photo', label: 'Image', hint: 'One image generation.' },
  { key: 'video', label: 'Video', hint: 'One video generation.' },
];

interface GenerationCostsResponse {
  costs: GenerationCosts;
  defaults: GenerationCosts;
  updatedAt: string | null;
}

type FormState = Record<ContentType, string>;

const toForm = (costs: GenerationCosts): FormState => ({
  text: String(costs.text),
  photo: String(costs.photo),
  video: String(costs.video),
});

export default function AdminGenerationPricing() {
  const [data, setData] = useState<GenerationCostsResponse | null>(null);
  const [form, setForm] = useState<FormState>({ text: '', photo: '', video: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    apiFetch<GenerationCostsResponse>('/admin/generation-costs')
      .then((r) => { setData(r); setForm(toForm(r.costs)); })
      .catch((e) => toast.error(e instanceof Error ? e.message : 'Failed to load generation prices'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const payload = {} as GenerationCosts;
    for (const { key, label } of TYPES) {
      const n = Number(form[key]);
      if (form[key].trim() === '' || !Number.isFinite(n) || n < 0) {
        toast.error(`${label}: enter a price of 0 or more`);
        return;
      }
      payload[key] = n;
    }
    try {
      setSaving(true);
      await apiFetch('/admin/generation-costs', { method: 'PUT', body: JSON.stringify(payload) });
      toast.success('Generation prices updated');
      load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-1 container mx-auto max-w-6xl py-10 px-4">
        <div className="mb-8">
          <h1 className="text-2xl font-bold">AI Generation Pricing</h1>
          <p className="text-sm text-muted-foreground">
            Credits charged per generated asset, for every workspace. Set them to match the models the generation workflow uses.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
        ) : (
          <Card className="p-6 max-w-2xl space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {TYPES.map(({ key, label, hint }) => (
                <div key={key} className="space-y-1.5">
                  <Label htmlFor={`cost-${key}`}>{label}</Label>
                  <Input
                    id={`cost-${key}`}
                    type="number"
                    min={0}
                    step={0.01}
                    value={form[key]}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground">
                    {hint} Default: {formatCredits(data?.defaults[key])}
                  </p>
                </div>
              ))}
            </div>

            <div className="text-xs text-muted-foreground space-y-1">
              <p>New prices apply to new generations within about a minute. A generation already running is charged at the price it started with.</p>
              <p>Only types that actually produce an asset are charged. Super admins are never charged.</p>
            </div>

            <div className="flex items-center justify-between gap-4">
              <p className="text-xs text-muted-foreground">
                {data?.updatedAt ? `Last updated ${new Date(data.updatedAt).toLocaleString()}` : 'Using the built-in defaults'}
              </p>
              <Button onClick={save} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save prices
              </Button>
            </div>
          </Card>
        )}
      </main>
    </div>
  );
}
